import React, { useState, useEffect } from 'react';
import EditModalWrapper from './EditModalWrapper';

const COURSE_LEVELS = [
    'High School', 'Diploma', 'Associate Degree', 'Bachelor\'s Degree',
    'Master\'s Degree', 'PhD', 'Vocational Training', 'Professional Certificate',
    'Other'
];

const AddEducationModal = ({
    isOpen,
    onClose,
    educationData = null, // null for new, object for editing
    onSave,
    isAddMode = true
}) => {
    const buildForm = (data) => ({
        courseTitle: data?.course_title || data?.qualification || data?.degree || '',
        institutionName: data?.institution_name || data?.university || data?.institution || '',
        courseLevel: data?.course_level || data?.degree_type || data?.field_of_study || '',
        startDate: data?.start_date || '',
        endDate: data?.end_date || data?.year_of_passout || '',
        description: data?.description || '',
    });

    const [form, setForm] = useState(buildForm(educationData));

    useEffect(() => {
        if (isOpen) {
            setForm(buildForm(educationData));
        }
    }, [isOpen, educationData]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!form.courseTitle.trim()) {
            alert('Course Title is required');
            return;
        }
        if (!form.institutionName.trim()) {
            alert('Institution Name is required');
            return;
        }
        if (!form.courseLevel) {
            alert('Course Level is required');
            return;
        }
        if (!form.startDate) {
            alert('Start Date is required');
            return;
        }
        if (!form.endDate) {
            alert('End Date or Expected is required');
            return;
        }

        setIsSubmitting(true);
        try {
            const educationEntry = {
                course_title: form.courseTitle.trim(),
                qualification: form.courseTitle.trim(),
                institution_name: form.institutionName.trim(),
                university: form.institutionName.trim(),
                course_level: form.courseLevel,
                degree_type: form.courseLevel,
                start_date: form.startDate,
                end_date: form.endDate,
                year_of_passout: form.endDate,
                description: form.description,
            };

            await onSave(educationEntry);
            onClose();
        } catch (error) {
            console.error('Error saving education details:', error);
            alert('Failed to save changes. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <EditModalWrapper
            isOpen={isOpen}
            onClose={onClose}
            title={isAddMode ? "Add Education Details" : "Edit Education Details"}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitLabel={isAddMode ? "Add" : "Update"}
            size="md"
        >
            <div className="space-y-5">
                {/* Course Title */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Course Title <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={form.courseTitle}
                        onChange={(e) => setForm({ ...form, courseTitle: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-slate-50 text-slate-900"
                        placeholder="Enter"
                        required
                    />
                </div>

                {/* Institution Name & Course Level */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Institution Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.institutionName}
                            onChange={(e) => setForm({ ...form, institutionName: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-slate-50 text-slate-900"
                            placeholder="Enter"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Course Level <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={form.courseLevel}
                            onChange={(e) => setForm({ ...form, courseLevel: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-slate-50 text-slate-900"
                            required
                        >
                            <option value="">Select</option>
                            {COURSE_LEVELS.map((level) => (
                                <option key={level} value={level}>{level}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Start Date & End Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Start Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={form.startDate}
                            onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            End Date or Expected <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={form.endDate}
                            onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                            required
                        />
                    </div>
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Description
                    </label>
                    <textarea
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        rows={4}
                        maxLength={512}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all resize-none bg-slate-50 text-slate-900"
                        placeholder="Description"
                    />
                    <div className="text-right text-xs text-slate-400 mt-1">{512 - form.description.length}</div>
                </div>
            </div>
        </EditModalWrapper>
    );
};

export default AddEducationModal;
